import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, Folder, Loader2, Pencil, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ title, sub, Icon, tone = 'blue', onClose, children, testId }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 p-3 backdrop-blur-sm dd-fade" onClick={onClose} data-testid={testId}>
    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl dd-pop" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start gap-3 px-5 pt-5">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tone === 'rose' ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1"><p className="text-base font-bold text-slate-900">{title}</p>{sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}</div>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const Btns = ({ onClose, onOk, ok, busy, disabled, danger, testId }) => (
  <div className="mt-5 flex justify-end gap-2">
    <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
    <button type="submit" onClick={onOk} disabled={busy || disabled} data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}{ok}</button>
  </div>
);

export const RenameDialog = ({ node, onClose, onSave }) => {
  const [name, setName] = useState(node.name);
  const dot = node.kind === 'file' ? node.name.lastIndexOf('.') : -1;
  return (
    <Shell title={`Rename ${node.kind === 'folder' ? 'folder' : 'document'}`} sub={node.name} Icon={Pencil} onClose={onClose} testId="dd-rename-dialog">
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim() && name !== node.name) onSave(name.trim()); }}>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onFocus={(e) => e.target.setSelectionRange(0, dot > 0 ? dot : e.target.value.length)} data-testid="dd-rename-input"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20" />
        <Btns onClose={onClose} ok="Rename" disabled={!name.trim() || name === node.name} testId="dd-rename-save" />
      </form>
    </Shell>
  );
};

export const MoveDialog = ({ node, headers, onClose, onMove }) => {
  const [targets, setTargets] = useState(null);
  const [pick, setPick] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    axios.get(`${API}/api/dropdrive/move-targets`, { params: { node_id: node.id }, headers })
      .then(({ data }) => setTargets(data.targets || [])).catch(() => setTargets([]));
  }, [node.id, headers]);
  const list = (targets || []).filter((t) => t.path.toLowerCase().includes(q.toLowerCase()));
  return (
    <Shell title="Move to…" sub={node.name} Icon={Folder} onClose={onClose} testId="dd-move-dialog">
      <div className="relative mb-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a folder" data-testid="dd-move-search" className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-400" /></div>
      <ul className="max-h-72 overflow-y-auto rounded-xl border border-slate-100 p-1" data-testid="dd-move-list">
        {targets === null && <li className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></li>}
        {targets && !list.length && <li className="py-8 text-center text-sm text-slate-400">No folders found</li>}
        {list.map((t) => (
          <li key={t.id || 'root'}>
            <button type="button" disabled={t.current} onClick={() => setPick(t)} data-testid={`dd-move-target-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${pick?.id === t.id ? 'bg-blue-600 text-white' : t.current ? 'text-slate-400' : 'text-slate-700 hover:bg-slate-50'}`}>
              <Folder className={`h-4 w-4 shrink-0 ${pick?.id === t.id ? 'text-white' : 'text-amber-500'}`} /><span className="min-w-0 flex-1 truncate">{t.path}</span>
              {t.current && <span className="text-[10px] font-bold uppercase">Current</span>}{pick?.id === t.id && <Check className="h-4 w-4" />}
            </button>
          </li>
        ))}
      </ul>
      <Btns onClose={onClose} onOk={() => pick && onMove(pick)} ok="Move here" disabled={!pick} testId="dd-move-confirm" />
    </Shell>
  );
};

export const ConfirmDialog = ({ title, body, ok = 'Delete', onClose, onOk }) => (
  <Shell title={title} Icon={AlertTriangle} tone="rose" onClose={onClose} testId="dd-confirm-dialog">
    <p className="text-sm text-slate-600">{body}</p>
    <Btns onClose={onClose} onOk={onOk} ok={ok} danger testId="dd-confirm-ok" />
  </Shell>
);
